import React from "react";
import {
    Grid,
    Theme,
    makeStyles,
    createStyles,
    Button,
    Typography
} from "@material-ui/core";
import { useDataUnlessForbidden } from "../services/api";
import IProjectHeader from "../models/IProjectHeader";
import UserProjectsGrid from "../components/UserProjectsGrid";
import { useAuth } from "../services/authentication";
import { SearchBox } from "../components/shared/SearchBox";
import IContainer from "./IContainer";
import { Redirect } from "react-router-dom";
import { useSnackbar } from "notistack";
import Skeleton from "@material-ui/lab/Skeleton";
import useDelayedEffect from "../hooks/useDelayedEffect";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            minHeight: "856px"
        },
        gridRoot: {
            flexGrow: 1,
            minHeight: "500px"
        },
        grid: {
            padding: theme.spacing(4, 0, 4, 0),
            position: "relative"
        },
        button: {
            margin: theme.spacing(8)
        },
        loadMoreButton: {
            marginLeft: "auto",
            marginRight: theme.spacing(3)
        },
        text: {
            padding: theme.spacing(2, 0, 0, 2)
        }
    })
);

const emptyProjects: IProjectHeader[] = [];

const UserProjectsContainer: React.FC<IContainer> = ({
    setSubtitle,
    setIsForbidden
}) => {
    const initialMaxToShow = 16;
    const { authState } = useAuth();
    const { enqueueSnackbar } = useSnackbar();
    const classes = useStyles();

    const isAdmin: boolean =
        authState.user !== null &&
        authState.user.isReservoirPortalAdministrator;

    const [projectsAll, , isProjectListForbidden] = useDataUnlessForbidden<
        IProjectHeader[]
    >("projects", () =>
        enqueueSnackbar(
            `We couldn't retrieve the list of projects for you. Please log out and try again.`,
            { variant: "error" }
        )
    );
    const [projectsSorted, setProjectsSorted] = React.useState(emptyProjects);
    const [projectsFiltered, setProjectsFiltered] = React.useState(
        emptyProjects
    );

    const [isFiltered, setFiltered] = React.useState(false);

    const [searchText, setSearchText] = React.useState("");
    const [showSearchbox, setShowSearchbox] = React.useState(false);

    const [maxToShow, setMaxToShow] = React.useState(initialMaxToShow);
    const [showMoreExtra, setShowMoreExtra] = React.useState(initialMaxToShow);
    const [showMoreRemaining, setShowMoreRemaining] = React.useState(0);
    const [showMoreDescription, setShowMoreDescription] = React.useState("");

    React.useEffect(() => {
        const sorted = (projectsAll || []).sort((a, b) => {
            return (
                new Date(a.created).getTime() - new Date(b.created).getTime()
            );
        });

        setProjectsSorted(sorted);
    }, [projectsAll]);

    useDelayedEffect(
        () => {
            const filtered = (projectsSorted || [])
                .filter(
                    p =>
                        (p.name !== null &&
                            p.name.toLowerCase().includes(searchText)) ||
                        (p.id !== null &&
                            p.id
                                .toString()
                                .toLowerCase()
                                .includes(searchText)) ||
                        (p.cr !== null &&
                            p.cr
                                .toString()
                                .toLowerCase()
                                .includes(searchText)) ||
                        (p.customerName !== null &&
                            p.customerName.toLowerCase().includes(searchText))
                )
                .reverse();

            setProjectsFiltered(filtered);

            if (searchText !== "") {
                setFiltered(true);
            }
        },
        256,
        [searchText, projectsSorted]
    );

    const showMoreHandler = React.useCallback(() => {
        setMaxToShow(f => f + showMoreExtra);
        setShowMoreExtra(f => f * 2);
    }, [showMoreExtra]);

    const onSearchChange = React.useCallback((text: string) => {
        if (text || []) {
            setSearchText(text.toLowerCase());
        }
    }, []);

    React.useEffect(() => setIsForbidden(isProjectListForbidden), [
        isProjectListForbidden
    ]);

    React.useEffect(() => {
        if (projectsAll === null || projectsAll!.length === 0) {
            // No projects, or still loading, so no point searching.
            setShowSearchbox(false);
        } else if (projectsAll!.length <= initialMaxToShow) {
            // User has so few projects that they appear on-screen in the initial batch.
            setShowSearchbox(false);
        } else {
            setShowSearchbox(true);
        }
    }, [projectsAll]);

    React.useEffect(() => {
        setMaxToShow(initialMaxToShow);
        setShowMoreExtra(initialMaxToShow);
    }, [searchText]);

    React.useEffect(
        () => setShowMoreRemaining(projectsFiltered.length - maxToShow),
        [projectsFiltered, maxToShow]
    );

    React.useEffect(
        () =>
            setShowMoreDescription(
                showMoreRemaining <= showMoreExtra
                    ? "Show remaining"
                    : `Show ${showMoreExtra} more projects`
            ),
        [showMoreRemaining, showMoreExtra]
    );

    React.useEffect(() => {
        if (projectsAll === null) {
            return;
            // NOOP.
        }

        if (projectsAll!.length === 0) {
            setSubtitle(
                `You haven't been added to any projects yet. Please contact your Tracerco team.`
            );
        } else if (projectsAll!.length === 1) {
            setSubtitle(`Your project is shown here.`);
        } else {
            setSubtitle(`You have ${projectsAll.length} projects.`);
        }
    }, [projectsAll]);

    if (projectsAll !== null && projectsAll!.length === 0) {
        return <Redirect to="/no-projects" />;
    } else
        return (
            <div className={classes.root}>
                {showSearchbox ? (
                    <SearchBox
                        onSearchChange={onSearchChange}
                        placeholder="Search Project Name, CR Number or Customer"
                        label="Search Projects"
                    />
                ) : (
                    <></>
                )}
                {projectsAll !== null && projectsAll!.length > 0 ? (
                    <div className={classes.gridRoot}>
                        <Grid
                            container
                            spacing={4}
                            className={classes.grid}
                            direction="row"
                        >
                            {UserProjectsGrid ? (
                                <>
                                    <UserProjectsGrid
                                        userProjects={projectsFiltered}
                                        maxToShow={maxToShow}
                                        enableAddUserFunction={isAdmin}
                                    />
                                    <div>
                                        {isFiltered &&
                                        projectsFiltered !== null &&
                                        projectsFiltered.length === 0 ? (
                                            <Typography
                                                variant="subtitle1"
                                                className={classes.text}
                                            >
                                                <cite>
                                                    No projects match that
                                                    search.
                                                </cite>
                                            </Typography>
                                        ) : null}
                                    </div>
                                </>
                            ) : (
                                <></>
                            )}
                        </Grid>
                    </div>
                ) : (
                    <Grid container spacing={4} className={classes.grid}>
                        <Skeleton
                            variant="rect"
                            width={"1232px"}
                            height={"56px"}
                            style={{ margin: "0 8px 8px 8px" }}
                        />
                        {[...Array(initialMaxToShow)].map((_, i) => (
                            <Skeleton
                                key={i}
                                variant="rect"
                                width={"292px"}
                                height={"330px"}
                                style={{ margin: "8px" }}
                            />
                        ))}
                    </Grid>
                )}
                {showMoreRemaining > 0 ? (
                    <div style={{ display: "flex", marginBottom: "32px" }}>
                        <Typography
                            variant="subtitle2"
                            className={classes.text}
                        >
                            There are {showMoreRemaining} further matches. Use
                            the button on the right to see more.
                        </Typography>
                        <Button
                            color="primary"
                            variant="outlined"
                            className={classes.loadMoreButton}
                            onClick={showMoreHandler}
                        >
                            {showMoreDescription}
                        </Button>
                    </div>
                ) : (
                    <></>
                )}
            </div>
        );
};

export { UserProjectsContainer };
